import _isNil from 'lodash.isnil';
import _isEmpty from 'lodash.isempty';

import {
  ICON_CONTENT_MAP,
  POLYGON_OUTLINE_LAYER_PREFIX,
  FONT_NAME,
  CONDITIONAL_OPERATORS,
  DEFAULT_MIN_ZOOM,
  DEFAULT_MAX_ZOOM,
  ZOOM_SCALE_DEFAULTS,
  FEATURE_STATUS_ID,
} from '../constants';
import { isPolygon } from './geom';
import {
  isDashedLineLayer,
  isPolygonOutlineMapboxLayer,
  isCircleLayer,
  isPolygonHighlightFillLayer,
} from './layers/types';

const DEFAULT_COLOR = '#79808b';
const DEFAULT_ICON_SIZE = 16;
const DEFAULT_LINE_WIDTH = 5;
const DEFAULT_POINT_ICON = ICON_CONTENT_MAP.circle;
const LOW_ZOOM_CIRCLE_MAX_ZOOM = 14;

export const isCategorizedByStatus = (categorizedAttributeLabel) =>
  categorizedAttributeLabel === FEATURE_STATUS_ID;

export const hasThresholdStyling = (layer) => {
  const { categorizedAttributeLabel } = layer.style;

  if (isCategorizedByStatus(categorizedAttributeLabel)) return false;

  return (
    categorizedAttributeLabel &&
    !layer.availableAttributes[categorizedAttributeLabel]?.permittedValues
  );
};

export const getSortKeyExpression = (activeEditingPlanId) => {
  if (!activeEditingPlanId) return 0;

  return ['case', ['==', ['get', 'plan_id'], activeEditingPlanId], 1, 0];
};

export const getLayerSymbols = (layer) => {
  const symbols = Object.values(layer.style?.symbols ?? {}) ?? [];

  if (layer.rasterId) {
    return symbols.map((symbol) => ({
      ...symbol,
      fillOpacity: 0,
      textOpacity: 0,
      lineOpacity: 0,
    }));
  }

  return symbols;
};

const getSymbolColor = (symbol, geomType) => {
  if (!symbol) return '#000';

  if (geomType === 'LineString') {
    return symbol.strokeColor;
  }

  return symbol.fillColor ?? symbol.strokeColor ?? '#000';
};

export const getConditionalExpression = (
  conditional,
  categorizedAttributeLabel,
  attributeValue,
) => [conditional, ['get', categorizedAttributeLabel], attributeValue];

const getCategorizedCaseExpression = (
  categorizedAttributeLabel,
  attrValsAndStyleVals,
  fallback,
  isUsingThresholdCategories,
) => {
  if (isUsingThresholdCategories) {
    const [, firstAttrStyle] = attrValsAndStyleVals[0];

    const firstCondition = [
      getConditionalExpression(CONDITIONAL_OPERATORS.EQUALS, categorizedAttributeLabel, null),
      firstAttrStyle,
    ];

    const middleConditions = attrValsAndStyleVals
      .sort((a, b) => a[0] - b[0])
      .flatMap(([attrVal, style]) => {
        const conditionalExpression = getConditionalExpression(
          CONDITIONAL_OPERATORS.LT,
          categorizedAttributeLabel,
          attrVal,
        );

        return [conditionalExpression, style];
      });

    const [lastAttrVal] = attrValsAndStyleVals[attrValsAndStyleVals.length - 1];

    const lastCondition = [
      getConditionalExpression(CONDITIONAL_OPERATORS.GTE, categorizedAttributeLabel, lastAttrVal),
      fallback,
    ];

    return ['case', ...firstCondition, ...middleConditions, ...lastCondition, fallback];
  }

  return [
    'match',
    ['to-string', ['get', categorizedAttributeLabel]],
    ...attrValsAndStyleVals.flat(),
    fallback,
  ];
};

const getZoomScaleSettings = (style, defaults) => {
  const minZoom = style.zoomScalingBounds?.min ?? defaults.minZoom;
  const maxZoom = style.zoomScalingBounds?.max ?? defaults.maxZoom;

  const { minFraction } = defaults;

  return { minZoom, maxZoom, minFraction };
};

export const buildCategorizedExpression = (style, valueCb, defaultValue) => {
  const { symbols, categorizedAttributeLabel, isUsingThresholdCategories } = style;

  const coercer = isUsingThresholdCategories ? Number : String;
  const attrValsAndStyleVals = Object.entries(symbols)
    // The "Other" style is filtered out here because it may be used for the fallback below
    // and we don't want to include it in `middleConditions` in `getCategorizedCaseExpression`.
    .filter(([attrVal]) => attrVal !== 'Other')
    .map(([category, symbol]) => [coercer(category), valueCb(symbol)]);

  const fallback = valueCb(symbols?.Other ?? {}) ?? defaultValue;

  return getCategorizedCaseExpression(
    categorizedAttributeLabel,
    attrValsAndStyleVals,
    fallback,
    isUsingThresholdCategories,
  );
};

const getNonHighlightSymbolColorExpression = (style, geomType) => {
  const { symbols, categorizedAttributeLabel } = style;
  if (_isEmpty(symbols)) return DEFAULT_COLOR;
  if (!categorizedAttributeLabel) return getSymbolColor(symbols.default, geomType);

  return buildCategorizedExpression(style, getSymbolColor, DEFAULT_COLOR, geomType);
};

export const getSymbolColorName = (mapboxLayerId) => {
  if (mapboxLayerId.endsWith('linestring')) return 'line-color';
  if (mapboxLayerId.startsWith('circle')) return 'circle-color';
  if (mapboxLayerId.endsWith('point')) return 'text-color';
  if (mapboxLayerId.endsWith('polygon')) return 'fill-color';

  throw new Error(`Cannot get mapbox color name for mapbox layer id: ${mapboxLayerId}`);
};

export const getSortKeyName = (mapboxLayerId) => {
  if (
    mapboxLayerId.endsWith('linestring') ||
    mapboxLayerId.startsWith(POLYGON_OUTLINE_LAYER_PREFIX)
  ) {
    return 'line-sort-key';
  }
  if (mapboxLayerId.startsWith('circle')) return 'circle-sort-key';
  if (mapboxLayerId.endsWith('point')) return 'symbol-sort-key';
  if (mapboxLayerId.endsWith('polygon')) return 'fill-sort-key';

  throw new Error(`Cannot get mapbox sort-key name for mapbox layer id: ${mapboxLayerId}`);
};

export const getSymbolColorExpression = (
  style,
  geomType,
  shouldHighlight,
  highlightColor = null,
) => {
  const colorExpression = getNonHighlightSymbolColorExpression(style, geomType);

  if (!shouldHighlight) return colorExpression;

  return ['coalesce', ['get', 'fillColor'], ['get', 'color'], highlightColor ?? colorExpression];
};

const getFinalStrokeWidth = (storedStrokeWidth, isHighlightingPolygon) => {
  if (isHighlightingPolygon) {
    return Math.max(3, storedStrokeWidth);
  }

  return storedStrokeWidth;
};

// symbolCbMax: a callback that will take a symbol and provide an appropriate max value
// symbolCbMin: a callback that will take a symbol and provide an appropriate min value
// style: style object
// defaultMax: the size that should be used as a fallback at max zoom
// minZoom: the zoom level at which the property starts increasing in size
// maxZoom: the zoom level at which the property stops increasing in size
const getZoomDependentExpression = (
  symbolCbMax,
  symbolCbMin,
  style,
  defaultMax,
  minZoom,
  maxZoom,
) => {
  const { symbols, categorizedAttributeLabel } = style;
  if (_isEmpty(symbols)) return defaultMax;

  if (!categorizedAttributeLabel) {
    const maxValue = symbolCbMax(symbols.default);
    const minValue = symbolCbMin(symbols.default);

    return ['interpolate', ['linear'], ['zoom'], minZoom, minValue, maxZoom, maxValue];
  }

  const coercer = style.isUsingThresholdCategories ? Number : String;

  const symbolsWithoutOther = Object.entries(symbols).filter(([category]) => category !== 'Other');

  const maxSizes = symbolsWithoutOther.map(([category, symbol]) => [
    coercer(category),
    symbolCbMax(symbol),
  ]);

  const minSizes = symbolsWithoutOther.map(([category, symbol]) => [
    coercer(category),
    symbolCbMin(symbol),
  ]);

  const MAX_SIZE_DEFAULT = symbolCbMax(symbols.Other);
  const MIN_SIZE_DEFAULT = symbolCbMin(symbols.Other);

  const minExpression = getCategorizedCaseExpression(
    categorizedAttributeLabel,
    minSizes,
    MIN_SIZE_DEFAULT,
    style.isUsingThresholdCategories,
  );

  const maxExpression = getCategorizedCaseExpression(
    categorizedAttributeLabel,
    maxSizes,
    MAX_SIZE_DEFAULT,
    style.isUsingThresholdCategories,
  );

  return ['interpolate', ['linear'], ['zoom'], minZoom, minExpression, maxZoom, maxExpression];
};

const getLineWidthExpression = (style, isPolygonFeature = false, isHighlightingPolygon = false) => {
  // TODO expand / customize these values. Potentially use getZoomDependentExpression
  if (style.autoInterpolate) {
    if (isPolygonFeature && !isHighlightingPolygon) {
      return [
        'interpolate',
        ['linear'],
        ['zoom'],
        // at zoom 0 use line width of 0
        0,
        0,
        // at zoom 8 use line width of 0
        8,
        0,
        // at zoom 12 use line width of 1
        12,
        1,
        // at zoom 22 use line width of 6
        22,
        6,
      ];
    }

    console.warn('No autoInterpolate expression for linestring lineWidth'); // eslint-disable-line no-console
  }

  const { minZoom, maxZoom, minFraction } = getZoomScaleSettings(style, ZOOM_SCALE_DEFAULTS.LINE);

  const symbolCbMax = (symbol) =>
    getFinalStrokeWidth(symbol?.strokeWeight ?? 1, isHighlightingPolygon);

  const symbolCbMin = (symbol) =>
    symbol?.minStrokeWeight
      ? symbol.minStrokeWeight * minFraction
      : Math.ceil(symbolCbMax(symbol) * minFraction);

  return getZoomDependentExpression(
    symbolCbMax,
    symbolCbMin,
    style,
    DEFAULT_LINE_WIDTH,
    minZoom,
    maxZoom,
  );
};

export const getPointIconSizeExpression = (layer) => {
  const iconSizeDenominator = isCircleLayer(layer) ? 2 : 1;

  const { minZoom, maxZoom, minFraction } = getZoomScaleSettings(
    layer.style,
    ZOOM_SCALE_DEFAULTS.POINT,
  );

  const symbolCbMax = (symbol) => (symbol?.iconSize ?? DEFAULT_ICON_SIZE) / iconSizeDenominator;

  const symbolCbMin = (symbol) =>
    symbol?.minIconSize
      ? (symbol.minIconSize * minFraction) / iconSizeDenominator
      : Math.ceil(symbolCbMax(symbol) * minFraction);

  return getZoomDependentExpression(
    symbolCbMax,
    symbolCbMin,
    layer.style,
    DEFAULT_ICON_SIZE,
    minZoom,
    maxZoom,
  );
};

const getPointIconExpression = ({
  symbols,
  categorizedAttributeLabel,
  isUsingThresholdCategories,
}) => {
  if (_isEmpty(symbols)) return DEFAULT_POINT_ICON;
  if (!categorizedAttributeLabel) return ICON_CONTENT_MAP[symbols.default.iconClass];

  return buildCategorizedExpression(
    { symbols, categorizedAttributeLabel, isUsingThresholdCategories },
    (c) => ICON_CONTENT_MAP[c.iconClass],
    DEFAULT_POINT_ICON,
  );
};

const getOpacityExpression = ({ style, key, shouldHighlight, layer, defaultOpacity = 1 }) => {
  // this is just for the "shadow" vector tile layer that
  // falls under the raster layer (to allow for hover state).
  // So the actual raster layers will have a normal opacity.
  if (layer.rasterId && !shouldHighlight) {
    return 0.01;
  }

  const valueCb = (symbol) => symbol[key] ?? defaultOpacity;

  const symbolOpacity = style.categorizedAttributeLabel
    ? buildCategorizedExpression(style, valueCb, defaultOpacity)
    : style.symbols.default[key] ?? defaultOpacity;

  const highlightOpacity = ['coalesce', ['get', 'opacity'], 1];
  const opacity = shouldHighlight ? highlightOpacity : symbolOpacity;

  if (isPolygonHighlightFillLayer(layer)) {
    return ['case', ['to-boolean', ['get', 'fillColor']], opacity, 0];
  }

  // TODO expand / customize these values. Potentially use getZoomDependentExpression
  if (style.autoInterpolate && !shouldHighlight) {
    if (key === 'fillOpacity') {
      return [
        'interpolate',
        ['linear'],
        ['zoom'],
        // at zoom 0 use opacity of 1
        0,
        1,
        // at zoom 14 (and up) use opacity of 0.4
        14,
        0.4,
      ];
    }

    if (key === 'lineOpacity') {
      return [
        'interpolate',
        ['linear'],
        ['zoom'],
        // at zoom 0 use opacity of 0.1
        0,
        0.1,
        12,
        0.5,
        // at zoom 16 use opacity of 0.7
        16,
        0.7,
        // at zoom 22 use opacity of 1
        22,
        1,
      ];
    }

    console.warn(`No autoInterpolate expression for ${key}.`); // eslint-disable-line no-console
  }

  return opacity;
};

const getCircleOpacityExpression = (style) =>
  style.transparencyAtLowZoom
    ? ['interpolate', ['linear'], ['zoom'], 10, 0.3, 12, 0.8, LOW_ZOOM_CIRCLE_MAX_ZOOM, 1]
    : 1;

export const getMapboxLayerStyleOptions = ({
  layer,
  visibility,
  shouldHighlight,
  highlightColor = null,
  activeEditingPlanId = null,
}) => {
  const { style } = layer;

  style.isUsingThresholdCategories = hasThresholdStyling(layer);

  const geomType = isPolygonOutlineMapboxLayer(layer) ? 'LineString' : layer.geomType;

  // https://docs.mapbox.com/help/tutorials/mapbox-gl-js-expressions/#add-a-zoom-expression

  const lineDasharray = isDashedLineLayer(layer) ? { lineDasharray: [2, 2.5] } : {};

  // polygon outline layers have the geom type of LineString
  const isPolygonFeature = isPolygonOutlineMapboxLayer(layer) || isPolygon(geomType);

  const isHighlightingPolygon = shouldHighlight && isPolygonFeature;

  switch (geomType) {
    case 'LineString':
      return {
        lineJoin: 'round',
        lineCap: 'round',
        lineWidth: getLineWidthExpression(style, isPolygonFeature, isHighlightingPolygon),
        lineColor: getSymbolColorExpression(style, geomType, shouldHighlight, highlightColor),
        lineSortKey: getSortKeyExpression(activeEditingPlanId),
        lineOpacity: getOpacityExpression({
          layer,
          style,
          key: 'lineOpacity',
          shouldHighlight,
        }),
        visibility,
        ...lineDasharray,
      };
    case 'Point':
      if (getLayerSymbols(layer).length === 0) {
        return {};
      }

      if (isCircleLayer(layer)) {
        return {
          circleColor: getSymbolColorExpression(style, geomType, shouldHighlight, highlightColor),
          circleOpacity: getCircleOpacityExpression(style),
          circleRadius: getPointIconSizeExpression(layer),
          circleSortKey: getSortKeyExpression(activeEditingPlanId),
          visibility: style.circleAtLowZoom ? visibility : 'none',
          maxzoom: style.circleAtLowZoom ? LOW_ZOOM_CIRCLE_MAX_ZOOM : DEFAULT_MAX_ZOOM,
        };
      }

      return {
        textFont: [`${FONT_NAME} Regular`],
        textField: getPointIconExpression(style),
        textColor: getSymbolColorExpression(style, geomType, shouldHighlight, highlightColor),
        textSize: getPointIconSizeExpression(layer),
        textAllowOverlap: true,
        textPadding: 0,
        textOpacity: getOpacityExpression({ layer, style, key: 'textOpacity', shouldHighlight }),
        symbolSortKey: getSortKeyExpression(activeEditingPlanId),
        visibility,
        minzoom: style.circleAtLowZoom ? LOW_ZOOM_CIRCLE_MAX_ZOOM : DEFAULT_MIN_ZOOM,
      };
    case 'Polygon':
    case 'MultiPolygon':
      return {
        fillColor: getSymbolColorExpression(style, geomType, shouldHighlight, highlightColor),
        fillOpacity: getOpacityExpression({ layer, style, key: 'fillOpacity', shouldHighlight }),
        fillSortKey: getSortKeyExpression(activeEditingPlanId),
        fillOutlineColor: 'rgba(0, 0, 0, 0)',
        visibility,
      };
    default:
      throw new Error(`Unrecognized geomType: ${geomType}`);
  }
};

/**
 * @param {string} dasharray dasharray with entries separated by commas and/or spaces
 * as allowed by https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-dasharray
 * @returns the same dasharray, but with entries separated by single spaces
 */
const standardizeDasharray = (dasharray) => dasharray.replace(/\s*,\s*/g, ' ');

const multiplyDasharray = (dasharray, factor) =>
  _isNil(dasharray)
    ? null
    : standardizeDasharray(dasharray)
        .split(' ')
        .map((x) => factor * x)
        .join(' ');

/**
 * Multiplies the symbol's the stroke weight and dasharray.
 * Because
 *  - we use rounded linecaps, and
 *  - rounded linecaps extend past the stroke endpoints by half the stroke width
 *    on both sides
 * we need to double the dasharray when doubling the stroke weight.
 *
 * Note: This doubling assumes unitless values for strokeWeight and dasharray.
 */
export const multiplyLinearSymbolProperties = (symbol, factor) => {
  const { strokeWeight: oldStrokeWeight, strokeDasharray: oldStrokeDasharray, ...others } = symbol;
  const strokeWeight = Number.isFinite(oldStrokeWeight)
    ? oldStrokeWeight * factor
    : oldStrokeWeight;
  const strokeDasharray = multiplyDasharray(oldStrokeDasharray, factor);
  return { strokeWeight, strokeDasharray, ...others };
};

// For layer lists
export const getCategorizedAttributeLabel = (
  layer,
  attributeValue,
  attributeLabel,
  useImperial,
  featureStatusLabelsById,
) => {
  const isOther = attributeValue === 'Other';

  if (isCategorizedByStatus(attributeLabel)) {
    return isOther ? attributeValue : featureStatusLabelsById[attributeValue];
  }

  if (!hasThresholdStyling(layer)) {
    return String(attributeValue);
  }

  const prefix = isOther ? '≥' : '<';

  // The final threshold style is always labeled as `≥ {max of preceeding threshold styles}`
  const penultimateThresholdValue = Math.max(
    ...Object.keys(layer.style.symbols || {})
      .filter((key) => key !== 'Other')
      .map((v) => Number(v)),
  );

  let value = isOther ? penultimateThresholdValue : attributeValue;

  // Total Length and Length are special names, so checking the label like this is fairly robust, though not ideal.
  if (['Total Length', 'Length'].includes(attributeLabel) && useImperial) {
    value = (Number(value) * 3.28084).toFixed(2);
  }

  return `${prefix} ${value}`;
};
